import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../ui/use-toast';
import { 
  Settings, 
  Shield,
  Clock,
  Bell,
  Database,
  Zap,
  Globe,
  Palette,
  Key,
  Server,
  Wifi,
  AlertTriangle,
  CheckCircle,
  Info,
  Save,
  RefreshCw,
  Download,
  Upload,
  Trash2,
  Archive,
  HardDrive,
  Activity,
  Users,
  Building2,
  UserCog,
  Crown,
  Monitor as MonitorIcon
} from 'lucide-react';
import { Team, User } from '../../../types';
import TeamManagement from '../TeamManagement';
import SupervisorManagement from '../../SupervisorManagement';
import ApiDebugger from '../../ApiDebugger';
import { BulkWorkSettings } from '../BulkWorkSettings';

interface SystemSettingsTabProps {
  teams: Team[];
  allUsers: User[];
  onRefresh: () => void;
  isLoading: boolean;
  currentUser: User;
}

export const SystemSettingsTab: React.FC<SystemSettingsTabProps> = ({
  teams,
  allUsers,
  onRefresh,
  isLoading,
  currentUser
}) => {
  const [activeTab, setActiveTab] = useState('general');
  const { toast } = useToast();

  // System settings state
  const [settings, setSettings] = useState({
    // General
    companyName: 'Munca la Schimb SRL',
    timezone: 'Europe/Bucharest',
    language: 'ro',
    dateFormat: 'DD/MM/YYYY',

    // Work Settings
    defaultWorkHours: 8,
    maxOvertimeHours: 4,
    breakDuration: 30,
    autoBreakAfter: 4,
    autoWorkCompletionTime: '19:00',

    // Notifications
    enableEmailNotifications: true,
    enablePushNotifications: false,
    breakAlertThreshold: 60,

    // Security
    passwordMinLength: 8,
    requirePasswordChange: false,
    sessionTimeout: 120,
    enableTwoFactor: false,

    // System
    enableAutoBackup: true,
    backupTime: '02:00',
    retentionDays: 90,
    enableDebugMode: false
  });

  const [isLoadingWorkCompletionTime, setIsLoadingWorkCompletionTime] = useState(false);
  const [savedWorkCompletionTime, setSavedWorkCompletionTime] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);

  // Load auto work completion time from backend
  useEffect(() => {
    const loadWorkCompletionTime = async () => {
      try {
        if (!currentUser || !currentUser.id) {
          console.log('No current user, skipping work completion time load');
          return;
        }

        console.log('Loading work completion time from backend...');
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api'}/systemsettings/work-completion-time`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('Load response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Loaded data from backend:', data);
          const timeString = `${String(data.hour).padStart(2, '0')}:${String(data.minute).padStart(2, '0')}`;
          console.log('Formatted time string for input:', timeString);
          setSettings(prev => ({ ...prev, autoWorkCompletionTime: timeString }));
          setSavedWorkCompletionTime(timeString);
          console.log('Settings updated with new time');
        }
      } catch (error) {
        console.error('Failed to load work completion time:', error);
      }
    };

    loadWorkCompletionTime();
  }, [currentUser]);

  // Handle work completion time update
  const handleUpdateWorkCompletionTime = async () => {
    console.log('handleUpdateWorkCompletionTime called');
    console.log('Current settings:', settings);
    console.log('Current user:', currentUser);

    setIsLoadingWorkCompletionTime(true);
    try {
      const [hour, minute] = settings.autoWorkCompletionTime.split(':').map(Number);
      console.log('Parsed time:', { hour, minute });

      if (!currentUser || !currentUser.id) {
        throw new Error('User not authenticated');
      }

      const requestBody = { hour, minute };
      console.log('Request body:', requestBody);
      console.log('User ID:', currentUser.id);

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api'}/systemsettings/work-completion-time`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Current-User-Id': currentUser.id.toString(),
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response:', response.status, errorText);
        let errorData: any = {};
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }
        throw new Error(errorData.message || errorData.error || `Server error: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Success response:', responseData);

      // Update the saved time tracker
      setSavedWorkCompletionTime(settings.autoWorkCompletionTime);

      toast({
        title: 'Setare salvată',
        description: `Ora de închidere automată a fost setată la ${settings.autoWorkCompletionTime}`,
      });

      console.log('Toast should be visible now');
    } catch (error: any) {
      console.error('Failed to update work completion time:', error);
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-a putut actualiza ora de închidere automată',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingWorkCompletionTime(false);
      console.log('Loading state set to false');
    }
  };

  // Handle settings update
  const handleSaveSettings = async () => {
    try {
      // Save other settings to localStorage (for now)
      // Work completion time has its own save button
      localStorage.setItem('systemSettings', JSON.stringify(settings));

      toast({
        title: 'Setări salvate',
        description: 'Setările sistemului au fost actualizate cu succes',
      });
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva setările',
        variant: 'destructive'
      });
    }
  };

  // Handle full system export
  const handleExportSystem = async () => {
    setIsExporting(true);
    try {
      if (!currentUser || !currentUser.id) {
        throw new Error('User not authenticated');
      }

      toast({
        title: 'Export în progres',
        description: 'Se exportă toate datele sistemului...',
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api'}/datamigration/export`, {
        method: 'POST',
        headers: {
          'X-Current-User-Id': currentUser.id.toString(),
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `system-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Export finalizat',
        description: 'Toate datele au fost exportate cu succes',
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut exporta datele',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Handle full system import
  const handleImportSystem = async () => {
    if (!importFile) {
      toast({
        title: 'Eroare',
        description: 'Vă rugăm să selectați un fișier de import',
        variant: 'destructive'
      });
      return;
    }

    setIsImporting(true);
    try {
      if (!currentUser || !currentUser.id) {
        throw new Error('User not authenticated');
      }

      toast({
        title: 'Import în progres',
        description: 'Se importă datele în sistem. Vă rugăm să așteptați...',
      });

      // Read file content
      const fileContent = await importFile.text();
      const importData = JSON.parse(fileContent);

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api'}/datamigration/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Current-User-Id': currentUser.id.toString(),
        },
        body: JSON.stringify(importData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || 'Import failed');
      }

      const result = await response.json();

      toast({
        title: 'Import finalizat',
        description: `S-au importat: ${result.usersImported} utilizatori, ${result.workEntriesImported} intrări de lucru, ${result.teamsImported} echipe`,
      });

      // Refresh page after successful import
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      console.error('Import error:', error);
      toast({
        title: 'Eroare la import',
        description: error.message || 'Nu s-au putut importa datele',
        variant: 'destructive'
      });
    } finally {
      setIsImporting(false);
      setImportFile(null);
    }
  };

  // Handle database backup
  const handleBackup = async () => {
    try {
      toast({
        title: 'Backup în progres',
        description: 'Se creează backup-ul bazei de date...',
      });

      // Simulate backup
      setTimeout(() => {
        toast({
          title: 'Backup complet',
          description: 'Backup-ul a fost creat cu succes',
        });
      }, 3000);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut crea backup-ul',
        variant: 'destructive'
      });
    }
  };

  // System info
  const systemInfo = {
    version: '2.5.0',
    buildDate: '2024-01-10',
    database: 'PostgreSQL 14.5',
    server: 'ASP.NET Core 7.0',
    client: 'React 18.2.0',
    signalR: 'Connected',
    uptime: '15 days, 3 hours',
    lastBackup: '2024-01-09 02:00',
    storageUsed: '2.4 GB',
    storageTotal: '10 GB'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Setări Sistem</h2>
        <Button
          onClick={handleSaveSettings}
          className="flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          Salvează Setări
        </Button>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Versiune</p>
                <p className="text-lg font-semibold">{systemInfo.version}</p>
              </div>
              <Info className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Uptime</p>
                <p className="text-lg font-semibold">{systemInfo.uptime}</p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Stocare</p>
                <p className="text-lg font-semibold">{systemInfo.storageUsed} / {systemInfo.storageTotal}</p>
              </div>
              <HardDrive className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conexiune</p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <p className="text-lg font-semibold">Online</p>
                </div>
              </div>
              <Wifi className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="work" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Muncă
          </TabsTrigger>
          <TabsTrigger value="teams" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Echipe
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Securitate
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            Sistem
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Setări Generale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nume Companie</Label>
                  <Input
                    value={settings.companyName}
                    onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Fus Orar</Label>
                  <select
                    value={settings.timezone}
                    onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Europe/Bucharest">Europe/București</option>
                    <option value="Europe/London">Europe/Londra</option>
                    <option value="America/New_York">America/New York</option>
                  </select>
                </div>
                
                <div>
                  <Label>Limbă</Label>
                  <select
                    value={settings.language}
                    onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="ro">Română</option>
                    <option value="en">English</option>
                  </select>
                </div>
                
                <div>
                  <Label>Format Dată</Label>
                  <select
                    value={settings.dateFormat}
                    onChange={(e) => setSettings({ ...settings, dateFormat: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Work Settings */}
        <TabsContent value="work" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Setări Program de Lucru</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Ore de lucru standard (pe zi)</Label>
                  <Input
                    type="number"
                    value={settings.defaultWorkHours}
                    onChange={(e) => setSettings({ ...settings, defaultWorkHours: parseInt(e.target.value) })}
                    min="1"
                    max="12"
                  />
                </div>
                
                <div>
                  <Label>Ore suplimentare maxime (pe zi)</Label>
                  <Input
                    type="number"
                    value={settings.maxOvertimeHours}
                    onChange={(e) => setSettings({ ...settings, maxOvertimeHours: parseInt(e.target.value) })}
                    min="0"
                    max="8"
                  />
                </div>
                
                <div>
                  <Label>Durată pauză standard (minute)</Label>
                  <Input
                    type="number"
                    value={settings.breakDuration}
                    onChange={(e) => setSettings({ ...settings, breakDuration: parseInt(e.target.value) })}
                    min="15"
                    max="120"
                  />
                </div>
                
                <div>
                  <Label>Pauză automată după (ore)</Label>
                  <Input
                    type="number"
                    value={settings.autoBreakAfter}
                    onChange={(e) => setSettings({ ...settings, autoBreakAfter: parseInt(e.target.value) })}
                    min="2"
                    max="6"
                  />
                </div>
              </div>

              {/* Auto Work Completion */}
              <div className="space-y-4 pt-4 border-t">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Închidere Automată Sesiuni de Lucru
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Configurează ora la care sistemul închide automat sesiunile de lucru neîncheiate.
                    Sesiunile vor fi limitate la 8 ore de muncă efectivă, iar timpul suplimentar va fi înregistrat ca overtime.
                  </p>
                </div>

                <div className="space-y-3">
                  {savedWorkCompletionTime && (
                    <Alert className="border-green-200 bg-green-50">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription>
                        <strong>Valoare salvată în baza de date:</strong> {savedWorkCompletionTime}
                        {settings.autoWorkCompletionTime !== savedWorkCompletionTime && (
                          <span className="text-orange-600 ml-2">(modificări nesalvate)</span>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                    <div>
                      <Label>Ora de închidere automată (format 24h, ora României)</Label>
                      <Input
                        type="time"
                        value={settings.autoWorkCompletionTime}
                        onChange={(e) => {
                          console.log('Time input changed to:', e.target.value);
                          setSettings({ ...settings, autoWorkCompletionTime: e.target.value });
                          console.log('Settings state updated');
                        }}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Format 24 ore - Exemplu: 19:00 pentru închidere la 7:00 PM (19:00)
                      </p>
                    </div>

                    <Button
                      onClick={handleUpdateWorkCompletionTime}
                      disabled={isLoadingWorkCompletionTime}
                      className="flex items-center gap-2"
                      variant="outline"
                    >
                      {isLoadingWorkCompletionTime ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Salvare...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Salvează Ora
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Funcționalitate:</strong> La ora configurată, sistemul va:
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Închide toate sesiunile de lucru active neîncheiate</li>
                      <li>Limita timpul de lucru la 8 ore efective + pauzele configurate</li>
                      <li>Crea înregistrări de overtime pentru timpul suplimentar lucrat</li>
                      <li>Actualiza statusul lucrătorilor la "neangajat"</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </div>

              {/* Notifications */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold">Notificări</h3>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Alertă pauză extinsă</Label>
                    <p className="text-sm text-gray-600">Notificare pentru pauze peste {settings.breakAlertThreshold} minute</p>
                  </div>
                  <Input
                    type="number"
                    value={settings.breakAlertThreshold}
                    onChange={(e) => setSettings({ ...settings, breakAlertThreshold: parseInt(e.target.value) })}
                    className="w-20"
                    min="30"
                    max="180"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Work Settings Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCog className="h-5 w-5" />
                Configurare în Masă pentru Muncitori
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BulkWorkSettings 
                allUsers={allUsers}
                onRefresh={onRefresh}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teams Management */}
        <TabsContent value="teams" className="space-y-4">
          <Tabs defaultValue="teams">
            <TabsList>
              <TabsTrigger value="teams" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Echipe
              </TabsTrigger>
              <TabsTrigger value="supervisors" className="flex items-center gap-2">
                <Crown className="h-4 w-4" />
                Supervisori
              </TabsTrigger>
            </TabsList>

            <TabsContent value="teams" className="mt-4">
              <TeamManagement
                isLoading={isLoading}
                onRefresh={onRefresh}
              />
            </TabsContent>

            <TabsContent value="supervisors" className="mt-4">
              <SupervisorManagement
                teams={teams}
                allUsers={allUsers}
                onRefresh={onRefresh}
                isLoading={isLoading}
              />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Setări Securitate</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label>Lungime minimă parolă</Label>
                  <Input
                    type="number"
                    value={settings.passwordMinLength}
                    onChange={(e) => setSettings({ ...settings, passwordMinLength: parseInt(e.target.value) })}
                    min="6"
                    max="20"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Solicită schimbare parolă la prima autentificare</Label>
                    <p className="text-sm text-gray-600">Utilizatorii noi trebuie să își schimbe parola</p>
                  </div>
                  <Switch
                    checked={settings.requirePasswordChange}
                    onCheckedChange={(checked) => setSettings({ ...settings, requirePasswordChange: checked })}
                  />
                </div>
                
                <div>
                  <Label>Timeout sesiune (minute)</Label>
                  <Input
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })}
                    min="15"
                    max="480"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Autentificare în doi pași</Label>
                    <p className="text-sm text-gray-600">Securitate suplimentară pentru conturi</p>
                  </div>
                  <Switch
                    checked={settings.enableTwoFactor}
                    onCheckedChange={(checked) => setSettings({ ...settings, enableTwoFactor: checked })}
                  />
                </div>
              </div>

              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  Modificarea setărilor de securitate poate afecta toți utilizatorii. 
                  Asigurați-vă că informați utilizatorii despre schimbări.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Settings */}
        <TabsContent value="system" className="space-y-4">
          {/* System Export/Import */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Migrare Sistem Completă
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription>
                  <strong>Export/Import Complet:</strong> Exportați toate datele sistemului într-un fișier JSON
                  pentru backup sau migrare către un alt server. Importul va adăuga datele lipsă fără a șterge datele existente.
                </AlertDescription>
              </Alert>

              {/* Export Section */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Date Sistem
                </h4>
                <p className="text-sm text-gray-600">
                  Exportă toate datele: utilizatori, echipe, proiecte, locații, intrări de lucru,
                  pauze, materiale, ore suplimentare, și setări sistem.
                </p>
                <Button
                  onClick={handleExportSystem}
                  disabled={isExporting}
                  className="w-full flex items-center justify-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Se exportă...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Exportă Toate Datele
                    </>
                  )}
                </Button>
              </div>

              <div className="border-t pt-4">
                {/* Import Section */}
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Import Date Sistem
                  </h4>
                  <p className="text-sm text-gray-600">
                    Importă date dintr-un fișier export JSON. Datele vor fi adăugate fără a șterge informațiile existente.
                  </p>

                  <div className="space-y-2">
                    <Label htmlFor="import-file">Selectează fișier JSON de import</Label>
                    <Input
                      id="import-file"
                      type="file"
                      accept=".json"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      disabled={isImporting}
                    />
                    {importFile && (
                      <p className="text-xs text-gray-500">
                        Fișier selectat: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={handleImportSystem}
                    disabled={isImporting || !importFile}
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2"
                  >
                    {isImporting ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Se importă...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Importă Date
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription>
                  <strong>Atenție:</strong> Importul va adăuga date noi în sistem. Asigurați-vă că
                  fișierul JSON este valid și provine dintr-un export recent al sistemului.
                  Utilizatorii importați vor avea parola implicită care trebuie schimbată la prima autentificare.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Backup Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5" />
                  Backup & Restore
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Backup automat</Label>
                    <p className="text-sm text-gray-600">Salvare automată a bazei de date</p>
                  </div>
                  <Switch
                    checked={settings.enableAutoBackup}
                    onCheckedChange={(checked) => setSettings({ ...settings, enableAutoBackup: checked })}
                  />
                </div>
                
                {settings.enableAutoBackup && (
                  <>
                    <div>
                      <Label>Ora backup</Label>
                      <Input
                        type="time"
                        value={settings.backupTime}
                        onChange={(e) => setSettings({ ...settings, backupTime: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <Label>Păstrare backup (zile)</Label>
                      <Input
                        type="number"
                        value={settings.retentionDays}
                        onChange={(e) => setSettings({ ...settings, retentionDays: parseInt(e.target.value) })}
                        min="7"
                        max="365"
                      />
                    </div>
                  </>
                )}
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleBackup}
                    className="flex-1 flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Backup Manual
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Restore
                  </Button>
                </div>
                
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Ultimul backup: {systemInfo.lastBackup}
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* System Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Informații Sistem
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Versiune</span>
                    <span className="font-medium">{systemInfo.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Build Date</span>
                    <span className="font-medium">{systemInfo.buildDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Database</span>
                    <span className="font-medium">{systemInfo.database}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Server</span>
                    <span className="font-medium">{systemInfo.server}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client</span>
                    <span className="font-medium">{systemInfo.client}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SignalR</span>
                    <Badge variant="success">{systemInfo.signalR}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Debug Mode */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Mod Debug
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Activare mod debug</Label>
                  <p className="text-sm text-gray-600">
                    Permite vizualizarea informațiilor detaliate despre sistem
                  </p>
                </div>
                <Switch
                  checked={settings.enableDebugMode}
                  onCheckedChange={(checked) => setSettings({ ...settings, enableDebugMode: checked })}
                />
              </div>
              
              {settings.enableDebugMode && (
                <Alert className="border-orange-200 bg-orange-50">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertDescription>
                    Modul debug este activ. Acesta poate afecta performanța și securitatea.
                    Dezactivați-l în producție.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* API Debugger */}
          {settings.enableDebugMode && (
            <ApiDebugger />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};